import { AxiosResponse } from 'axios';
import { axiosRequest } from '@/common/api/instance';
import {
  Meta,
  PaginatedResponse,
  RequestConfig,
  Response as ApiResponse,
} from '@/types/api/response.types';
import {
  BulkUpload,
  BulkUploadResponse,
  LocationParams,
  SyncedOrganisationCount,
  Vendor,
  VendorBody,
  VendorItem,
  VendorItemParams,
  VendorParams,
  VendorSync,
} from '@/types/api/vendors.types';
import { ProjectImageBody } from '@/types/api/project.types';

const resource = '/vendors';

export const createVendor = async (
  data: VendorBody,
): Promise<AxiosResponse<ApiResponse<Vendor>>> => {
  return axiosRequest().post(`${resource}/`, data);
};

export const getVendors = async (
  params?: VendorParams,
): Promise<AxiosResponse<PaginatedResponse<Vendor[]>>> => {
  return axiosRequest().get(`${resource}`, {
    params,
    paramsSerializer: (p) => {
      const searchParams = new URLSearchParams();

      for (const [key, value] of Object.entries(p ?? {})) {
        if (Array.isArray(value)) {
          for (const item of value) {
            if (item !== undefined && item !== null) {
              searchParams.append(key, item);
            }
          }
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      }
      return searchParams.toString();
    },
  });
};

export const updateVendor = async (
  id: string,
  data: VendorBody,
): Promise<AxiosResponse<ApiResponse<Vendor>>> => {
  return axiosRequest().put(`${resource}/${id}`, data);
};

export const deleteVendor = async (id: string): Promise<AxiosResponse<ApiResponse<Meta>>> => {
  return axiosRequest().delete(`${resource}/${id}`);
};

export const syncVendors = async (
  data: VendorSync,
): Promise<AxiosResponse<ApiResponse<Vendor[]>>> => {
  return axiosRequest().post(`${resource}/sync`, data);
};

export const uploadImage = async (
  data: ProjectImageBody,
): Promise<AxiosResponse<ApiResponse<Vendor>>> => {
  return axiosRequest().patch(`${resource}/${data.id}/images`, data.body);
};

export const deleteImage = async (id: string): Promise<AxiosResponse<ApiResponse<Meta>>> => {
  return axiosRequest().delete(`${resource}/${id}/images`);
};

/**
 * Context: a 404 here is a refused count, so a central save stops on it; listed, it takes the
 * generic error toast rather than the organisation re-fetch an unlisted logged-in 404 triggers.
 */
const syncedOrganisationCountConfig: Partial<RequestConfig> = { handlesErrors: [404] };

export const getSyncedOrganisationCount = async (
  vendorId: string,
): Promise<AxiosResponse<ApiResponse<SyncedOrganisationCount>>> => {
  return axiosRequest().get(
    `${resource}/${vendorId}/synced-organisations/count`,
    syncedOrganisationCountConfig,
  );
};

export const getVendorItems = async (
  id: string,
  params?: VendorItemParams,
): Promise<AxiosResponse<PaginatedResponse<VendorItem[]>>> => {
  return axiosRequest().get(`${resource}/${id}/items`, { params });
};

export const bulkUploadItems = async (
  data: BulkUpload,
): Promise<AxiosResponse<ApiResponse<BulkUploadResponse>>> => {
  return axiosRequest().post(`${resource}/${data.id}/items`, data.body);
};

export const getVendorCities = async (
  params?: LocationParams,
): Promise<AxiosResponse<ApiResponse<string[]>>> => {
  return axiosRequest().get(`${resource}/cities`, { params });
};

export const getVendorSuburbs = async (
  params?: LocationParams,
): Promise<AxiosResponse<ApiResponse<string[]>>> => {
  return axiosRequest().get(`${resource}/suburbs`, { params });
};
